/**
 * <PinThreadButton /> — "Save conversation" for the chat panel (M25.3).
 *
 * Chat is ephemeral by default (commandment 27). Pinning is the
 * user-controlled exception: this button snapshots the current
 * conversation's transcript to a named `chat_thread` so it survives
 * logout and can be reopened from /ai/threads or shared (M25.6).
 *
 * The name is collected with a prompt — deliberately lightweight;
 * a richer naming modal is a later polish.
 */

import { BookmarkPlus, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";
import type { ChatThread } from "../../lib/api";
import type { ChatTurn } from "./types";

export interface PinThreadButtonProps {
  /** The conversation to snapshot. */
  messages: ChatTurn[];
  /** The ephemeral session id it was pinned from (may be null). */
  sessionId: string | null;
  /** Whether this conversation is already pinned (shows "Saved"). */
  alreadyPinned: boolean;
  /** Called with the created thread once the save succeeds. */
  onPinned: (thread: ChatThread) => void;
}

export function PinThreadButton({
  messages,
  sessionId,
  alreadyPinned,
  onPinned,
}: PinThreadButtonProps) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const empty = messages.length === 0;

  async function pin() {
    if (saving || empty || alreadyPinned) return;
    const name = window
      .prompt(t("app.ptb_name_prompt"))
      ?.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      const thread = await api.saveChatThread({
        name,
        transcript: messages as unknown as Record<string, unknown>[],
        session_id: sessionId,
      });
      onPinned(thread);
    } catch (e) {
      setError((e as Error)?.message ?? t("app.ptb_couldnt_save"));
    } finally {
      setSaving(false);
    }
  }

  if (alreadyPinned) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
        <Check size={12} /> {t("app.ptb_saved")}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void pin()}
      disabled={saving || empty}
      title={
        error ??
        (empty
          ? t("app.ptb_nothing")
          : t("app.ptb_save_title"))
      }
      aria-label={t("app.ptb_save_aria")}
      className="inline-flex items-center gap-1 rounded-md p-1 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary disabled:opacity-40"
    >
      {saving ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <BookmarkPlus size={14} />
      )}
    </button>
  );
}
